import { useEffect, useState, useCallback, useRef } from "react";
import { Flame, Plus, Pencil, Trash2, Upload, Download, Eye, FileText, Loader2, CheckCircle2, AlertTriangle, Sparkles } from "lucide-react";
import { api, useAuth, formatApiError, downloadFile, canDo } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import EmployeePicker from "../components/EmployeePicker";
import ExportDialog from "../components/ExportDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";

const STATUTS = {
  valide: { label: "Valide", classes: "bg-emerald-950 text-emerald-400 border-emerald-700" },
  a_recycler: { label: "À recycler", classes: "bg-amber-950 text-amber-400 border-amber-700" },
  depasse: { label: "Expiré", classes: "bg-red-950 text-red-400 border-red-700" },
  inconnu: { label: "Inconnu", classes: "bg-gray-800 text-gray-300 border-gray-600" },
};

export function SsiapBadge({ niveau }) {
  return (
    <span className="inline-flex items-center rounded-full border border-red-700 bg-red-950 px-2.5 py-0.5 text-xs font-bold text-red-400" data-testid={`ssiap-badge-${niveau}`}>
      SSIAP {niveau}
    </span>
  );
}

function Jours({ jours }) {
  if (jours === null || jours === undefined) return <span className="text-xs text-muted-foreground">—</span>;
  const cls = jours < 0 ? "text-red-400" : jours <= 240 ? "text-amber-400" : "text-muted-foreground";
  const txt = jours < 0 ? `expiré depuis ${-jours} j` : `${jours} j`;
  return <span className={`text-xs font-mono ${cls}`}>{txt}</span>;
}

const EMPTY_ENTRY = { niveau: "1", date_recyclage: "", date_renouvellement: "" };

export default function Ssiap() {
  const { user } = useAuth();
  const readOnly = !canDo(user, "ssiap", "modifier");
  const [agents, setAgents] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [manageAgent, setManageAgent] = useState(null);
  const [addOpen, setAddOpen] = useState(false);
  const [addForm, setAddForm] = useState({ emp_id: "", ...EMPTY_ENTRY });
  const [entryForm, setEntryForm] = useState(EMPTY_ENTRY);
  const [editingHid, setEditingHid] = useState(null);
  const [preview, setPreview] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(() => {
    api.get("/ssiap").then((r) => setAgents(r.data.agents)).catch((e) => toast.error(formatApiError(e))).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    api.get("/employees").then((r) => setAllEmployees(r.data.employees)).catch(() => {});
  }, [load]);

  const kpis = agents.reduce((acc, a) => {
    for (const s of a.ssiap) acc[s.statut] = (acc[s.statut] || 0) + 1;
    return acc;
  }, {});

  const openPreview = async (c) => {
    try {
      const r = await api.get(`/certificats/${c.id}/download`, { responseType: "blob" });
      setPreview({ url: URL.createObjectURL(r.data), filename: c.filename });
    } catch (e) {
      toast.error(formatApiError(e, "Aperçu impossible"));
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !manageAgent) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      await api.post(`/employees/${manageAgent.id}/certificats?categorie=ssiap`, form);
      toast.success("Certificat SSIAP téléversé — analyse en cours");
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Échec du téléversement"));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeCert = async (c) => {
    if (!window.confirm(`Supprimer le certificat "${c.filename}" ?`)) return;
    try {
      await api.delete(`/certificats/${c.id}`);
      toast.success("Certificat supprimé");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const applyAnalyse = async (c) => {
    try {
      await api.post(`/certificats/${c.id}/appliquer`);
      toast.success("Dates SSIAP mises à jour depuis le certificat");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const saveEntry = async () => {
    if (!manageAgent) return;
    setSaving(true);
    const payload = { niveau: parseInt(entryForm.niveau), date_recyclage: entryForm.date_recyclage || null, date_renouvellement: entryForm.date_renouvellement || null };
    try {
      if (editingHid) {
        await api.put(`/employees/${manageAgent.id}/ssiap/${editingHid}`, payload);
        toast.success("Certification modifiée");
      } else {
        await api.post(`/employees/${manageAgent.id}/ssiap`, payload);
        toast.success("Certification SSIAP ajoutée");
      }
      setEntryForm(EMPTY_ENTRY);
      setEditingHid(null);
      load();
      setManageAgent(null);
      setTimeout(() => {}, 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const removeEntry = async (hid) => {
    if (!window.confirm("Supprimer cette certification SSIAP ?")) return;
    try {
      await api.delete(`/employees/${manageAgent.id}/ssiap/${hid}`);
      toast.success("Certification supprimée");
      load();
      setManageAgent(null);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const addAgent = async () => {
    if (!addForm.emp_id) {
      toast.error("Choisissez un salarié");
      return;
    }
    setSaving(true);
    try {
      await api.post(`/employees/${addForm.emp_id}/ssiap`, {
        niveau: parseInt(addForm.niveau),
        date_recyclage: addForm.date_recyclage || null,
        date_renouvellement: addForm.date_renouvellement || null,
      });
      toast.success("Agent SSIAP ajouté");
      setAddOpen(false);
      setAddForm({ emp_id: "", ...EMPTY_ENTRY });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const currentAgent = manageAgent ? agents.find((a) => a.id === manageAgent.id) || manageAgent : null;

  return (
    <div className="space-y-6" data-testid="ssiap-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-red-500 mb-1">Sécurité incendie</p>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight flex items-center gap-3" style={{ fontFamily: "Outfit" }}>
            <Flame className="h-8 w-8 text-red-500" /> Certificats <span className="text-red-500">SSIAP</span>
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Validité 3 ans · rappels automatiques à 8, 6, 4 et 2 mois avant expiration</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <ExportDialog type="ssiap" filename="export_ssiap.xlsx" testid="export-ssiap-button" />
          {!readOnly && (
            <Button onClick={() => setAddOpen(true)} className="bg-red-600 hover:bg-red-700" data-testid="add-ssiap-button">
              <Plus className="h-4 w-4 mr-2" /> Ajouter un agent SSIAP
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Agents SSIAP", value: agents.length, color: "text-red-400", testid: "ssiap-kpi-agents" },
          { label: "Certifications valides", value: kpis.valide || 0, color: "text-emerald-400", testid: "ssiap-kpi-valides" },
          { label: "À recycler (< 8 mois)", value: kpis.a_recycler || 0, color: "text-amber-400", testid: "ssiap-kpi-recycler" },
          { label: "Expirées", value: kpis.depasse || 0, color: "text-red-400", testid: "ssiap-kpi-expirees" },
        ].map((k) => (
          <div key={k.testid} className="rounded-xl border border-red-900/50 bg-red-950/20 p-4" data-testid={k.testid}>
            <p className={`text-2xl font-extrabold font-mono ${k.color}`}>{k.value}</p>
            <p className="text-xs text-muted-foreground mt-1">{k.label}</p>
          </div>
        ))}
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden hidden md:block">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ssiap-table">
            <thead>
              <tr className="border-b border-border text-left text-xs font-mono uppercase tracking-wider text-muted-foreground">
                <th className="px-4 py-3">Agent</th>
                <th className="px-4 py-3">Service</th>
                <th className="px-4 py-3"><span className="text-red-400">Certification</span></th>
                <th className="px-4 py-3">Dernier recyclage</th>
                <th className="px-4 py-3">Renouvellement</th>
                <th className="px-4 py-3">Délai</th>
                <th className="px-4 py-3">Statut</th>
                <th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground">Chargement...</td></tr>}
              {!loading && agents.length === 0 && <tr><td colSpan={8} className="px-4 py-10 text-center text-muted-foreground" data-testid="ssiap-empty">Aucun agent SSIAP enregistré.</td></tr>}
              {!loading && agents.flatMap((a) =>
                a.ssiap.map((s) => (
                  <tr key={`${a.id}-${s.hid}`} className="border-b border-border/50 hover:bg-secondary/40 transition-colors" data-testid={`ssiap-row-${a.id}-${s.hid}`}>
                    <td className="px-4 py-3"><span className="font-medium">{a.nom}</span> {a.prenom}</td>
                    <td className="px-4 py-3 text-muted-foreground">{a.service}</td>
                    <td className="px-4 py-3"><SsiapBadge niveau={s.niveau} /></td>
                    <td className="px-4 py-3 font-mono text-xs">{s.date_recyclage || "—"}</td>
                    <td className="px-4 py-3 font-mono text-xs">{s.date_renouvellement || "—"}</td>
                    <td className="px-4 py-3"><Jours jours={s.jours_restants} /></td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUTS[s.statut].classes}`}>{STATUTS[s.statut].label}</span>
                    </td>
                    <td className="px-4 py-3 text-right whitespace-nowrap">
                      <Button variant="ghost" size="icon" title="Certificats PDF" onClick={() => { setManageAgent(a); setEntryForm(EMPTY_ENTRY); setEditingHid(null); }} data-testid={`ssiap-manage-${a.id}`}>
                        <FileText className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="md:hidden space-y-2" data-testid="ssiap-cards">
        {loading && <p className="py-8 text-center text-muted-foreground">Chargement...</p>}
        {!loading && agents.length === 0 && <p className="py-8 text-center text-muted-foreground">Aucun agent SSIAP enregistré.</p>}
        {!loading && agents.flatMap((a) =>
          a.ssiap.map((s) => (
            <div key={`${a.id}-${s.hid}`} className="rounded-xl border border-border bg-card p-4 space-y-2.5" data-testid={`ssiap-card-${a.id}-${s.hid}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-semibold">{a.nom} {a.prenom}</p>
                  <p className="text-xs text-muted-foreground">{a.service}</p>
                </div>
                <SsiapBadge niveau={s.niveau} />
              </div>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-mono text-muted-foreground">
                <span>Recyclage : {s.date_recyclage || "—"}</span>
                <span>Renouvellement : {s.date_renouvellement || "—"}</span>
                <Jours jours={s.jours_restants} />
              </div>
              <div className="flex items-center justify-between gap-2">
                <span className={`inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs font-semibold ${STATUTS[s.statut].classes}`}>{STATUTS[s.statut].label}</span>
                <Button variant="outline" size="sm" className="h-10" onClick={() => { setManageAgent(a); setEntryForm(EMPTY_ENTRY); setEditingHid(null); }} data-testid={`ssiap-manage-m-${a.id}`}>
                  <FileText className="h-4 w-4 mr-1.5" /> Certificats
                </Button>
              </div>
            </div>
          ))
        )}
      </div>

      <Dialog open={!!manageAgent} onOpenChange={(o) => !o && setManageAgent(null)}>
        <DialogContent className="max-w-2xl" data-testid="ssiap-manage-dialog">
          {currentAgent && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Flame className="h-5 w-5 text-red-500" /> {currentAgent.prenom} {currentAgent.nom}
                </DialogTitle>
                <DialogDescription>{currentAgent.service} — certifications SSIAP et certificats PDF</DialogDescription>
              </DialogHeader>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Certifications</Label>
                {currentAgent.ssiap.map((s) => (
                  <div key={s.hid} className="flex items-center justify-between rounded-lg border border-border px-3 py-2" data-testid={`ssiap-entry-${s.hid}`}>
                    <div className="flex items-center gap-3 flex-wrap">
                      <SsiapBadge niveau={s.niveau} />
                      <span className="text-xs font-mono">{s.date_recyclage || "—"} → {s.date_renouvellement || "—"}</span>
                      <Jours jours={s.jours_restants} />
                    </div>
                    {!readOnly && (
                      <div className="flex items-center">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => { setEditingHid(s.hid); setEntryForm({ niveau: String(s.niveau), date_recyclage: s.date_recyclage || "", date_renouvellement: s.date_renouvellement || "" }); }} data-testid={`ssiap-edit-${s.hid}`}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7 hover:text-red-400" onClick={() => removeEntry(s.hid)} data-testid={`ssiap-delete-${s.hid}`}>
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
                {!readOnly && (
                  <div className="flex flex-wrap items-end gap-2 rounded-lg border border-dashed border-border p-3">
                    <div className="space-y-1">
                      <Label className="text-xs">Niveau</Label>
                      <Select value={entryForm.niveau} onValueChange={(v) => setEntryForm({ ...entryForm, niveau: v })}>
                        <SelectTrigger className="w-28 bg-background" data-testid="ssiap-form-niveau"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="1">SSIAP 1</SelectItem>
                          <SelectItem value="2">SSIAP 2</SelectItem>
                          <SelectItem value="3">SSIAP 3</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Dernier recyclage</Label>
                      <Input type="date" value={entryForm.date_recyclage} onChange={(e) => setEntryForm({ ...entryForm, date_recyclage: e.target.value })} className="bg-background w-40" data-testid="ssiap-form-recyclage" />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Renouvellement <span className="text-muted-foreground">(auto +3 ans)</span></Label>
                      <Input type="date" value={entryForm.date_renouvellement} onChange={(e) => setEntryForm({ ...entryForm, date_renouvellement: e.target.value })} className="bg-background w-40" data-testid="ssiap-form-renouvellement" />
                    </div>
                    <Button size="sm" onClick={saveEntry} disabled={saving} className="bg-red-600 hover:bg-red-700" data-testid="ssiap-form-submit">
                      {editingHid ? "Modifier" : "Ajouter"}
                    </Button>
                    {editingHid && <Button size="sm" variant="ghost" onClick={() => { setEditingHid(null); setEntryForm(EMPTY_ENTRY); }}>Annuler</Button>}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-wider text-muted-foreground">Certificats PDF</Label>
                {!readOnly && (
                  <div>
                    <input ref={fileRef} type="file" accept=".pdf,application/pdf" className="hidden" onChange={upload} data-testid="ssiap-cert-input" />
                    <Button size="sm" variant="outline" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="ssiap-cert-upload">
                      {uploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
                      Téléverser un certificat SSIAP
                    </Button>
                  </div>
                )}
                {currentAgent.certificats?.length === 0 && <p className="text-xs text-muted-foreground">Aucun certificat SSIAP téléversé.</p>}
                {currentAgent.certificats?.map((c) => (
                  <div key={c.id} className="rounded-lg border border-border" data-testid={`ssiap-cert-${c.id}`}>
                    <div className="flex items-center justify-between px-3 py-2">
                      <button onClick={() => openPreview(c)} className="flex items-center gap-2 text-sm min-w-0 text-left hover:text-red-400 transition-colors">
                        <FileText className="h-4 w-4 text-red-400 shrink-0" />
                        <span className="truncate hover:underline underline-offset-2">{c.filename}</span>
                        <span className="text-xs text-muted-foreground font-mono shrink-0">{c.created_at?.slice(0, 10)}</span>
                      </button>
                      <div className="flex items-center shrink-0">
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => openPreview(c)} data-testid={`ssiap-cert-view-${c.id}`}><Eye className="h-3.5 w-3.5" /></Button>
                        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => downloadFile(`/certificats/${c.id}/download`, c.filename).catch(() => toast.error("Téléchargement impossible"))} data-testid={`ssiap-cert-dl-${c.id}`}><Download className="h-3.5 w-3.5" /></Button>
                        {!readOnly && (
                          <Button variant="ghost" size="icon" className="h-7 w-7 hover:text-red-400" onClick={() => removeCert(c)} data-testid={`ssiap-cert-del-${c.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                        )}
                      </div>
                    </div>
                    {c.analyse?.statut === "en_cours" && (
                      <p className="flex items-center gap-2 text-xs text-muted-foreground px-3 pb-2"><Loader2 className="h-3.5 w-3.5 animate-spin text-red-400" /> Analyse automatique en cours...</p>
                    )}
                    {c.analyse?.statut === "termine" && (
                      <div className="mx-3 mb-2 rounded-lg border border-red-900/50 bg-red-950/20 p-3 space-y-1.5" data-testid={`ssiap-analysis-${c.id}`}>
                        <p className="flex items-center gap-1.5 text-xs font-semibold text-red-400"><Sparkles className="h-3.5 w-3.5" /> Analyse automatique (confiance : {c.analyse.confiance})</p>
                        <p className="text-xs">Détecté : <span className="font-medium">{c.analyse.prenom || "?"} {c.analyse.nom || "?"}</span>
                          {c.analyse.niveau && <> · <span className="text-red-400 font-semibold">SSIAP {c.analyse.niveau}</span></>}
                          {c.analyse.date_formation && <> · recyclage <span className="font-mono">{c.analyse.date_formation}</span></>}
                          {c.analyse.date_validite && <> · renouvellement <span className="font-mono">{c.analyse.date_validite}</span></>}
                        </p>
                        {!c.analyse.correspondance && (
                          <p className="flex items-center gap-1.5 text-xs text-red-400"><AlertTriangle className="h-3.5 w-3.5" /> Le nom détecté ne correspond pas à cet agent.</p>
                        )}
                        {c.analyse.appliquee ? (
                          <p className="flex items-center gap-1.5 text-xs text-emerald-400"><CheckCircle2 className="h-3.5 w-3.5" /> Dates appliquées à la certification</p>
                        ) : !readOnly && (c.analyse.date_formation || c.analyse.date_validite) && (
                          <Button size="sm" variant="outline" onClick={() => applyAnalyse(c)} data-testid={`ssiap-apply-${c.id}`}>
                            <CheckCircle2 className="h-3.5 w-3.5 mr-1.5" /> Appliquer les dates
                          </Button>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent className="max-w-md" data-testid="ssiap-add-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Flame className="h-5 w-5 text-red-500" /> Ajouter un agent SSIAP</DialogTitle>
            <DialogDescription>Associez une certification SSIAP à un salarié existant.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Salarié *</Label>
              <EmployeePicker employees={allEmployees} value={addForm.emp_id}
                onChange={(v) => setAddForm({ ...addForm, emp_id: v })} testid="ssiap-add-employee" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="space-y-2">
                <Label>Niveau</Label>
                <Select value={addForm.niveau} onValueChange={(v) => setAddForm({ ...addForm, niveau: v })}>
                  <SelectTrigger className="bg-background" data-testid="ssiap-add-niveau"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="1">SSIAP 1</SelectItem>
                    <SelectItem value="2">SSIAP 2</SelectItem>
                    <SelectItem value="3">SSIAP 3</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Recyclage</Label>
                <Input type="date" value={addForm.date_recyclage} onChange={(e) => setAddForm({ ...addForm, date_recyclage: e.target.value })} className="bg-background" data-testid="ssiap-add-recyclage" />
              </div>
              <div className="space-y-2">
                <Label>Renouvellement</Label>
                <Input type="date" value={addForm.date_renouvellement} onChange={(e) => setAddForm({ ...addForm, date_renouvellement: e.target.value })} className="bg-background" data-testid="ssiap-add-renouvellement" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddOpen(false)}>Annuler</Button>
            <Button onClick={addAgent} disabled={saving} className="bg-red-600 hover:bg-red-700" data-testid="ssiap-add-submit">
              {saving ? "Enregistrement..." : "Ajouter"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!preview} onOpenChange={(o) => { if (!o) { if (preview) URL.revokeObjectURL(preview.url); setPreview(null); } }}>
        <DialogContent className="max-w-4xl" data-testid="ssiap-preview-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><FileText className="h-5 w-5 text-red-400" /> {preview?.filename}</DialogTitle>
            <DialogDescription>Aperçu du certificat PDF</DialogDescription>
          </DialogHeader>
          {preview && <iframe src={preview.url} title={preview.filename} className="w-full h-[70vh] rounded-lg border border-border bg-white" data-testid="ssiap-preview-frame" />}
        </DialogContent>
      </Dialog>
    </div>
  );
}
